import MarketingLayout from '../../components/marketing/MarketingLayout';
import { Card, Title, Text } from "@tremor/react";
import { useState } from 'react';
import BookingWidget from '../../components/BookingWidget';
import ServicePageNavigation from '../../components/ServicePageNavigation';
import { colors } from '../../lib/colors';

export default function CriticalIllnessInsurance() {
  const [showBookingWidget, setShowBookingWidget] = useState(false);

  return (
    <MarketingLayout>
      <section className="py-16 px-4 bg-white dark:bg-gray-900">
        <div className="max-w-4xl mx-auto relative">
          {/* Service Navigation */}
          <ServicePageNavigation currentSlug="critical-illness-insurance" />
          {/* Header */}
          <div className="text-center mb-12 pt-4"> {/* Added padding top */}
            <Title className="text-4xl font-bold text-gray-800 dark:text-white mb-4">Critical Illness Insurance</Title>
            <Text className="text-xl text-gray-600 dark:text-gray-400">
              Financial Support When Facing a Serious Health Diagnosis
            </Text>
          </div>

          <div className="space-y-8">
            {/* What is Critical Illness Insurance? */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">What is Critical Illness Insurance?</Title>
              <Text className="text-gray-600 dark:text-gray-300">
                Critical Illness (CI) insurance provides a one-time, lump-sum cash payment if you are diagnosed with a specific life-altering illness covered by the policy (such as cancer, heart attack, stroke, kidney failure, etc.) and survive a waiting period (typically 30 days).
              </Text>
              <Text className="text-gray-600 dark:text-gray-300 mt-2">
                Unlike disability insurance which replaces income, the CI benefit can be used for any purpose the policyholder chooses. It's designed to alleviate the financial stress that often accompanies a serious diagnosis, allowing you to focus on recovery without worrying about immediate costs or income gaps.
              </Text>
            </Card>

            {/* How It Works */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">How It Works</Title>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-300 space-y-2 mt-4">
                <li>
                  <strong>Policy Selection:</strong> You choose a coverage amount (lump-sum benefit) and the type of policy (e.g., covering a basic set of illnesses or a more comprehensive list).
                </li>
                <li>
                  <strong>Pay Premiums:</strong> You pay regular premiums to keep the policy active. Premiums can be level for a term or for life, depending on the policy.
                </li>
                <li>
                  <strong>Diagnosis:</strong> You are diagnosed with one of the critical illnesses specifically listed in your policy.
                </li>
                <li>
                  <strong>Survival Period:</strong> You must survive for a specified period following the diagnosis (usually 30 days).
                </li>
                <li>
                  <strong>Receive Lump Sum:</strong> After the survival period and claim approval, you receive the full lump-sum benefit amount, typically tax-free.
                </li>
                 <li>
                  <strong>Use Funds Freely:</strong> The money is yours to use as needed – for medical expenses not covered by provincial health plans, travel for treatment, home modifications, replacing lost income, paying off debt, or simply reducing financial worry.
                </li>
              </ol>
            </Card>

            {/* Key Benefits */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Key Benefits</Title>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Financial Flexibility:</strong> Lump-sum payment provides immediate cash for any need during a health crisis.</li>
                <li><strong>Focus on Recovery:</strong> Reduces financial stress, allowing you and your family to concentrate on treatment and healing.</li>
                <li><strong>Covers Gaps:</strong> Helps pay for costs not covered by government health plans or disability insurance (e.g., experimental treatments, private nursing, travel).</li>
                <li><strong>Income Replacement Option:</strong> Can be used to replace lost income for yourself or a caregiving spouse.</li>
                <li><strong>Tax-Free Benefit:</strong> The lump sum received is generally not considered taxable income.</li>
                <li><strong>Return of Premium Option:</strong> Some policies offer riders that return some or all premiums paid if no claim is made by a certain age or upon death.</li>
              </ul>
            </Card>

            {/* Considerations */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Considerations</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Important factors to evaluate:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li><strong>Covered Conditions:</strong> Policies explicitly list the illnesses covered. Ensure the definitions and covered conditions meet your expectations. More comprehensive policies cover more conditions but cost more.</li>
                <li><strong>Survival Period:</strong> The requirement to survive for a period (e.g., 30 days) after diagnosis.</li>
                <li><strong>Pre-existing Conditions:</strong> May affect eligibility or coverage for certain conditions.</li>
                <li><strong>Cost:</strong> Premiums increase with age, coverage amount, and the number of conditions covered.</li>
                <li><strong>One-Time Payout:</strong> Unlike disability insurance, CI typically pays out once per lifetime for a major illness (though some policies offer multiple payouts for unrelated conditions or partial payouts for less severe conditions).</li>
              </ul>
            </Card>

            {/* Suitability */}
            <Card className="p-6 bg-gray-50 dark:bg-gray-800 shadow-lg">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-3">Is Critical Illness Insurance Right for You?</Title>
               <Text className="text-gray-600 dark:text-gray-300 mb-3">Critical illness insurance can be valuable for:</Text>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-300 space-y-1">
                <li>Anyone concerned about the financial impact of a major illness.</li>
                <li>Individuals with high deductibles or potential out-of-pocket medical costs.</li>
                <li>Self-employed individuals or those with limited sick leave/disability coverage.</li>
                <li>People with family histories of critical illnesses like cancer or heart disease.</li>
                <li>Those seeking an extra layer of financial protection beyond life and disability insurance.</li>
              </ul>
            </Card>

            {/* Call to Action */}
            <div className="text-center mt-12">
              <Title className="text-2xl font-semibold text-gray-800 dark:text-white mb-4">Gain Financial Peace of Mind During Recovery</Title>
              <Text className="text-gray-600 dark:text-gray-400 mb-6 max-w-xl mx-auto">
                Prepare for the unexpected financial challenges of a serious illness. Let's explore critical illness coverage options to protect your finances.
              </Text>
              <button
                onClick={() => setShowBookingWidget(true)}
                className="px-8 py-3 rounded-md text-lg font-semibold text-white transition-transform duration-150 ease-in-out hover:scale-105 active:scale-95"
                style={{ background: `linear-gradient(135deg, ${colors.brand.primary} 0%, ${colors.brand.accent} 100%)` }}
              >
                Discuss Critical Illness Options
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Widget Modal */}
      {showBookingWidget && (
        <BookingWidget /> // Removed onClose prop
      )}
    </MarketingLayout>
  );
}
